import React, { useState, useCallback, useMemo, useEffect } from 'react';
import moment from "moment-timezone";
import { Select, DatePicker } from 'antd';
import { Popover } from 'antd';
import HelpIcon from '@material-ui/icons/Help';

import styles from './AnalyticChartDateOptions.module.css';

const DATE_RANGE_PICKER = 'DATE_RANGE_PICKER';

const Option = Select.Option;

export default function AnalyticsChartDateOptions({ defaultSelected={}, showRangeOption=false, intervalOptions=null, otherOptions=null, controlledOtherOptionVal={}, rangeOptions=null, onChange=(val)=>console.log("onChange:", val), onOtherOptionsChange=(val)=>console.log('onOtherOptionsChange:', val), minDate=moment('2019-02-08'), showHelperText=false, helperText=null }) {
	const [ selectedOptions, setSelectedOptions ] = useState(defaultSelected); // { interval: '1M', range: '3Y' } || { interval: '1M', range: { type: DATE_RANGE_PICKER, moments: [ moment(), moment() ] } }
	const controlledSelectedOptions = useMemo(() => ({ ...selectedOptions, ...controlledOtherOptionVal }), [ selectedOptions, controlledOtherOptionVal ]);
	const [ datePickerOpen, setDatePickerOpen ] = useState(false);

	const dateOptions = useMemo(() => {
		const filterOptions = [
			{
				label: 'Interval',
				name: 'interval',
				options: intervalOptions || [
					{ label: 'Monthly', value: '1M', timeMs: 1000*60*60*24*30,  visibleRangeOptions: ['3M', '6M', '12M', '1Y', 'PAST_1Y'] },
					{ label: 'Yearly',  value: '1Y', timeMs: 1000*60*60*24*365, visibleRangeOptions: ['CALENDAR_ALL', 'CONTRACT_ALL'] },
				]
			}
		];
		if(showRangeOption) {
			filterOptions.push({
				label: 'Range',
				name: 'range',
				options: rangeOptions || [
					{ label: 'Last 3 months', value: '3M', moments: [moment().subtract(2, 'month').startOf('month'), moment().startOf('day')] },
					{ label: 'Last 6 months', value: '6M', moments: [moment().subtract(5, 'month').startOf('month'), moment().startOf('day')] },
					{ label: 'Last 12 months', value: '12M', moments: [moment().subtract(11, 'month').startOf('month'), moment().startOf('day')] },
					{ label: 'This year', value: '1Y', moments: [moment().startOf('year'), moment().startOf('day')] },
					{ label: 'Last year', value: 'PAST_1Y', moments: [moment().subtract(1, 'year').startOf('year'), moment().subtract(1, 'year').endOf('year').startOf('day')] },
					{ label: 'All calendar years', value: 'CALENDAR_ALL', moments: [moment('2019-02-01'), moment().startOf('day')] },
					{ label: 'All contract years', value: 'CONTRACT_ALL', moments: [moment('2019-02-01'), moment().startOf('day')] },
				]
			});
		}
		if(otherOptions?.length) {
			// const otherOptions = [
			// 	{
			// 		label: 'Chart',
			// 		name: 'chart',
			// 		options: [
			// 			{ label: 'Other Data', value: 'OTHER_DATA' },
			// 			{ label: 'Production', value: 'PRODUCTION' }
			// 		]
			// 	}
			// ];
			filterOptions.unshift(...otherOptions);
		}
		return filterOptions;
	}, [ intervalOptions, rangeOptions, showRangeOption, otherOptions ]);

	// return date range picker time diff in ms
	const getMomentsDiff = useCallback(moments => moments[1].diff(moments[0]), []);

	const getIntervalUnit = useCallback(interval => {
		if(/D$/gi.test(interval)) { return 'date' }
		if(/M$/gi.test(interval)) { return 'month' }
		if(/Y$/gi.test(interval)) { return 'year' }
		return 'date';
	}, []);

	const trimMomentTime = useCallback(_moment => moment(_moment).startOf(getIntervalUnit(selectedOptions.interval)), [ selectedOptions, getIntervalUnit ]);

	const handleDateRangePickerChange = useCallback((optionName, moments) => {
		if(!moments?.length) { return } // if user clear date range picker, skip
		const newState = { ...selectedOptions, [optionName]: { type: DATE_RANGE_PICKER, moments } };
		newState[optionName].moments = [ trimMomentTime(moments[0]), trimMomentTime(moments[1]) ];
		setSelectedOptions(newState);
	}, [ selectedOptions, trimMomentTime ]);

	// handle interval option and other options clicked
	const handleOptionClick = useCallback(({ name: optionName, value }) => {
		// this is for other options
		if(optionName !== 'interval') { return onOtherOptionsChange({ [optionName]: value }) }
		// below lines are for interval option
		let newState = { ...selectedOptions, [optionName]: value };
		// reselect interaval or range if the interval cannot fit in the date range
		if(showRangeOption) {
			// interval option
			const intervalDateOption = dateOptions.find(dateOption => dateOption.name === 'interval');
			const intervalOption = intervalDateOption.options.find(option => option.value === newState[intervalDateOption.name]);
			// range option
			const rangeDateOption = dateOptions.find(dateOption => dateOption.name === 'range');
			// rangeOption = { label: 'Last 3 months', value: '3M', moments: [moment, moment] } || { type: DATE_RANGE_PICKER, moments: [moment, moment] }
			const rangeOption = newState[rangeDateOption.name]?.type === DATE_RANGE_PICKER ? newState[rangeDateOption.name] : rangeDateOption.options.find(option => option.value === newState[rangeDateOption.name]);
			// skip if not found interval and range option
			if(!intervalOption || !rangeOption) { return }
			// reselect range if user change interval option
			const availableRangeOptions = rangeDateOption.options.filter(option => intervalOption.visibleRangeOptions.includes(option.value));
			if(availableRangeOptions.find(option => option.value === 'CALENDAR_ALL')) { // default to all calendar year
				newState.range = availableRangeOptions.find(option => option.value === 'CALENDAR_ALL').value;
			} else {
				newState.range = availableRangeOptions.findLast(option => getMomentsDiff(option.moments) > intervalOption.timeSpan).value;
			}
		}
		setSelectedOptions(newState);
	}, [ selectedOptions, dateOptions, getMomentsDiff, showRangeOption, onOtherOptionsChange ]);

	const datePickerControlledValue = useMemo(() => {
		if(selectedOptions.range.type !== DATE_RANGE_PICKER) {
			const moments = dateOptions.find(dateOption => dateOption.name === 'range')?.options.find(option => option.value === selectedOptions.range)?.moments;
			return moments;
		}
		return selectedOptions.range.moments;
	}, [ dateOptions, selectedOptions.range ]);

	const selectedControlledValue = useMemo(() => typeof(selectedOptions.range) === 'string' ? selectedOptions.range : 'Custom', [ selectedOptions ]);

	const optionActiveClass = useCallback((optionName, option) => controlledSelectedOptions[optionName] === option.value ? styles.active : '', [ controlledSelectedOptions ]);

	const handleRangeDropdownChange = useCallback(val => setSelectedOptions({ ...selectedOptions, range: val }), [ selectedOptions ]);

	useEffect(() => {
		onChange({ interval: selectedOptions.interval, range: selectedOptions.range, moments: datePickerControlledValue });
	}, [ selectedOptions.interval, selectedOptions.range, datePickerControlledValue, onChange ]);

	// enabled date range to be between 2019-02-08 to now
	const datePickerDisabledDate = useCallback(current => current < minDate.startOf('day') || current > moment().endOf('day'), [ minDate ]);

	return (
		<div className={styles.optionContainer}>
			{dateOptions.map(dateOption => (
				<div key={dateOption.name} className={styles.dateOption}>
					<span className={styles.optionLabel}>{dateOption.label}</span>
					{dateOption.name !== 'range' && (
						<div className={styles.options}>
							{dateOption.options.map(option => <div key={option.value} className={`${styles.option} ${optionActiveClass(dateOption.name, option)}`} onClick={() => handleOptionClick({ name: dateOption.name, value: option.value })}>{option.label}</div>)}
						</div>
					)}
					{dateOption.name === 'range' && (
						<React.Fragment key={dateOption.name}>
							<Select className={styles.select} value={selectedControlledValue} onChange={handleRangeDropdownChange}>{dateOption.options.filter(option => intervalOptions.find(option => option.value === selectedOptions.interval).visibleRangeOptions.includes(option.value)).map(option => <Option key={option.value} value={option.value}>{option.label}</Option>)}</Select>
							<DatePicker.RangePicker open={datePickerOpen} onChange={(moments) => handleDateRangePickerChange(dateOption.name, moments)} picker={getIntervalUnit(selectedOptions.interval)} className={`${styles.datePicker} ${datePickerOpen ? styles.show : ''}`} placement="bottomRight" value={datePickerControlledValue} onOpenChange={open => setDatePickerOpen(open)} allowClear={false} disabledDate={datePickerDisabledDate} popupClassName={styles.dateRangePickerPopup} />
						</React.Fragment>
					)}
				</div>
			))}
			{showHelperText && <Popover content={helperText}><HelpIcon className={styles.popoverIcon} /></Popover>}
		</div>
	)
}